import { getStripeScriptUrl } from '@/constants/change-plan.js';

const MESSAGES = {
  error: 'We can\'t seem to access our servers. Refresh to try again.',
};

class StripeElements {
  constructor(store, apiKey) {
    this.apiKey = apiKey;
    this.store = store;
    this.stripe = null;

    // Persistent form vars
    this.element = null;
    this.elements = null;
    this.returnUrl = '';
  }

  // Helper functions
  setLoadingState() {
    this.store.commit('stripeElements/setError', '');
    this.store.commit('stripeElements/setIsLoading', true);
  }

  resetForm() {
    this.returnUrl = '';
    this.elements = null;
    this.element = null;
  }

  async init() {
    if (process.client && this.apiKey) {
      if (this.stripe) {
        return Promise.resolve();
      }

      if (window.Stripe) {
        this.stripe = window.Stripe(this.apiKey);
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('src', getStripeScriptUrl());
        document.getElementsByTagName('head')[0].appendChild(script);

        script.onload = () => {
          this.stripe = window.Stripe(this.apiKey);
          resolve();
        };
      });
    }

    return Promise.resolve();
  }

  initForm(clientSecret, returnUrl) {
    if (!(this.stripe && clientSecret && returnUrl)) {
      this.store.commit('stripeElements/setError', MESSAGES.error);
      return;
    }

    const options = {
      clientSecret,
      appearance: {},
    };

    this.resetForm();
    this.returnUrl = returnUrl;
    this.elements = this.stripe.elements(options);
    this.element = this.elements.create('payment');

    this.element.on('ready', () => (
      this.store.commit('stripeElements/setIsReady', true)
    ));
  }

  retrievePaymentIntent(clientSecret) {
    if (!this.stripe) {
      this.store.commit('stripeElements/setError', MESSAGES.error);
      return;
    }

    this.setLoadingState();

    this.stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      this.store.commit('stripeElements/setPaymentIntent', paymentIntent);
      this.store.commit('stripeElements/setIsLoading', false);
    }).catch(() => {
      this.store.commit('stripeElements/setError', MESSAGES.error);
      this.store.commit('stripeElements/setIsLoading', false);
    });
  }

  async confirmPayment() {
    try {
      this.setLoadingState();

      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: this.returnUrl,
        },
      });

      if (error) {
        console.log(error); // TODO -- Remove
        this.store.commit('stripeElements/setError', error.message);
        return error.message;
      }

      // If no error reset the stripe elements
      this.reset();
    } catch (error) {
      console.log(error); // TODO -- Remove
      this.store.commit('stripeElements/setError', error.message);
      return error;
    } finally {
      this.store.commit('stripeElements/setIsLoading', false);
    }

    return null;
  }
}

export default (context, inject) => {
  const { store, $config } = context;
  const apiKey = $config.stripePublicKey;
  const stripeElements = new StripeElements(store, apiKey);

  inject('stripeElements', stripeElements);
  context.$stripeElements = stripeElements;
};
